// D80 Tier 2 — Floating toolbar for the active TipTap Table block.
// Shown beneath the cursor whenever the selection is inside a table.
// Buttons: add row above/below, add column left/right, toggle header
// row, delete row, delete column, delete whole table.
import { BubbleMenu } from '@tiptap/react/menus';
import {
  RowsIcon, ColumnsIcon, Trash2, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, Heading2,
} from 'lucide-react';

// Module-level stable references — passing fresh `options` / `shouldShow`
// per render makes BubbleMenu's internal useEffect (deps include those)
// dispatch a `updateOptions` transaction every render, which in our
// editor cascades into a React state-update loop. Keeping these stable
// is mandatory.
const TABLE_BUBBLE_OPTIONS = { placement: 'bottom', offset: 8 };
const tableShouldShow = ({ editor }) => editor.isActive('table');

export function TableToolbar({ editor }) {
  if (!editor) return null;
  const btn = (onClick, testid, Icon, title) => (
    <button
      type="button" onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="p-1.5 rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground transition-colors"
      data-testid={testid} title={title} aria-label={title}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
  return (
    <BubbleMenu
      editor={editor}
      options={TABLE_BUBBLE_OPTIONS}
      shouldShow={tableShouldShow}
    >
      <div
        className="flex items-center gap-0.5 bg-popover border border-border rounded-lg shadow-2xl p-1 backdrop-blur-lg"
        data-testid="table-toolbar"
      >
        {btn(() => editor.chain().focus().addRowBefore().run(), 'tbl-row-above', ChevronUp, 'Add row above')}
        {btn(() => editor.chain().focus().addRowAfter().run(), 'tbl-row-below', ChevronDown, 'Add row below')}
        {btn(() => editor.chain().focus().deleteRow().run(), 'tbl-row-del', RowsIcon, 'Delete row')}
        <span className="w-px h-4 bg-border mx-0.5" />
        {btn(() => editor.chain().focus().addColumnBefore().run(), 'tbl-col-left', ChevronLeft, 'Add column left')}
        {btn(() => editor.chain().focus().addColumnAfter().run(), 'tbl-col-right', ChevronRight, 'Add column right')}
        {btn(() => editor.chain().focus().deleteColumn().run(), 'tbl-col-del', ColumnsIcon, 'Delete column')}
        <span className="w-px h-4 bg-border mx-0.5" />
        {btn(() => editor.chain().focus().toggleHeaderRow().run(), 'tbl-header-row', Heading2, 'Toggle header row')}
        {btn(() => editor.chain().focus().deleteTable().run(), 'tbl-delete', Trash2, 'Delete table')}
      </div>
    </BubbleMenu>
  );
}
